<template>
    <div class="timestamp" :class="cssClasses" v-if="session">
        <div class="sessionId" v-if="!isLive">
            <Button @click.prevent="newSession">
                New
            </Button>
            Session: <span v-text="sessionId"></span>
            <input type="text" placeholder="JOIN SESSION ID" v-model="newId">
            <Button @click.prevent="joinSession">
                Join
            </Button>
        </div>

        <a href="#" @click.prevent="openFS" class="fs">
            <svg viewBox="0 0 24 24">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 
                    7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
            </svg>
            <svg viewBox="0 0 24 24">
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 
                    11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
            </svg>
        </a>

        <LogoComponent v-if="logo && !message" class="logo" />

        <div class="message" v-if="message">
            <p v-html="message.message"></p>
        </div>

        <CounterPage v-if="counter !== false" class="timer" />

        <div class="topics">
            <TopicsComponent class="list" />
            <CountdownCirclePage class="timer" />
        </div>

        <TotalProgress class="progress" />

        <div class="twitch">
            <TwitchFollowersList class="followers" :showButton="showButton" />
            <TwitchMessages class="twitchMessages" :showButton="showButton" />
        </div>

    </div>
</template>

<script>
import CountdownCirclePage from './CountdownCirclePage.vue';
import CounterPage from './CounterPage.vue';
import LogoComponent from '@/components/LogoComponent.vue'
import TopicsComponent from '@/components/TopicsComponent.vue';
import TotalProgress from '@/components/TotalProgress';
import TwitchFollowersList from '@/components/TwitchFollowersList.vue'
import TwitchMessages from '@/components/TwitchMessages.vue'

import Button from "@/components/ButtonComponent.vue"

import { getCookie } from '@/cookies.js'

export default {
    name: 'TimestampPage',
    components: {
        Button,
        LogoComponent,
        CounterPage,
        CountdownCirclePage,
        TopicsComponent,
        TotalProgress,
        TwitchFollowersList,
        TwitchMessages,
    },

    props: {
        skipSession: { default: false },
    },

    data() {
        return {
            logo: true,
            showButton: false,
            state: "normal",
            newId: "",
        }
    },
    methods: {
        joinSession() {
            this.$store.dispatch('sessions/fetch', this.newId)
        },
        newSession() {
            this.$store.dispatch("sessions/create")
        },
        openFS() {
            if (!document.fullscreenElement) {
                document.body.requestFullscreen();
                document.body.setAttribute("fullscreen", "");
            } else {
                document.exitFullscreen();
                document.body.removeAttribute("fullscreen");
            }
        }
    },

    computed: {
        counter() {
            return this.$store.state.counter.counter
        },
        cssClasses() {
            if (this.orientation == null) {
                return {
                    normal: this.state == "normal",
                    vertical: this.state == "vertical",
                }
            }
            return {
                normal: this.orientation.value == "normal",
                vertical: this.orientation.value == "vertical",
            }
        },
        isLive() {
            return this.$store.state.counter.isLive
        },
        message() {
            return this.$store.getters['messages/liveMessage']
        },
        messages() {
            return this.$store.state.messages.all
        },
        orientation() {
            return this.$store.getters['settings/name']('orientation')
        },
        session() {
            return this.$store.state.sessions.current
        },
        sessionId() {
            if (!this.session) {
                return null
            }
            return this.session.session_id
        },
        token() {
            return this.$store.state.token
        },
    },

    watch: {
        session: {
            immediate: true,
            handler() {
                if (!this.session) {
                    return
                }
                this.$store.dispatch("getTwitchToken")
                this.$store.dispatch("settings/fetch", "orientation")
            },
        },
    },

    mounted() {
        if (this.session != null || this.skipSession) {
            return
        }
        let sessionId = getCookie('session')
        if (sessionId) {
            this.$store.dispatch("sessions/fetch", sessionId)
        } else {
            this.$store.dispatch("sessions/create")
        }
    },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import "@/assets/_variables.scss";

.fs {
    position: absolute;
    z-index: 1000;
    width: calc(var(--unit) * 2);
    height: calc(var(--unit) * 2);

    top: var(--unit);
    right: var(--unit);
    transition: transform .3s;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;

    svg {
        fill: white;
    }

    svg:nth-child(2) {
        display: none;
    }

    [fullscreen] & svg:nth-child(1) {
        display: none;
    }

    [fullscreen] & svg:nth-child(2) {
        display: inline-block;
    }
}

.timestamp {
    --unit: 1vw;
    --height: calc(#{math.div(100 * 9, 16)} * var(--unit));
    --width: calc(100 * var(--unit));
    width: var(--width);
    height: var(--height);

    @media screen and (min-aspect-ratio: 16/9) {
        --unit: #{math.div(16, 9)}vh;
    }

    display: grid;
    overflow: hidden;
    background: $background;
    position: relative;

    // SIZING
    padding: calc(var(--unit) * 1) calc(var(--unit) * 3);
    margin: auto;

    &.vertical {
        --unit: #{math.div(16, 9)}vw;
        --width: calc(#{math.div(100 * 9, 16)} * var(--unit));
        --height: calc(100 * var(--unit));

        @media screen and (min-aspect-ratio: 9/16) {
            --unit: 1vh;
        }

        grid-template-areas: "logo"
        "timer"
        "topics"
        "progress"
        ;
        grid-template-rows: 4fr 2fr 10fr 1fr;

        .twitch {
            display: none;
        }

        .topics {
            grid-template-columns: 1fr;
            grid-template-rows: 2fr 1fr;
        }
    }

    &.normal {
        grid-template-areas:
            "logo timer"
            "topics topics"
            "progress progress"
        ;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 3fr 6fr 1fr;

        .twitch {
            display: none;
        }
    }

    >.timer {
        grid-area: timer;
        margin: auto;
    }

    .logo {
        grid-area: logo;
        max-width: 100%;
        margin: auto;
    }

    .topics {
        grid-area: topics;
        display: grid;
        background-color: #1b1b21;
        border-radius: var(--unit);
        padding: var(--unit);
        height: 100%;
        overflow: hidden;
        grid-template-columns: 2fr 1fr;

        .timer {
            margin: auto;
        }

        .list {
            width: 100%;
            margin: auto;
        }
    }

    >.progress {
        grid-area: progress;
    }
}

.timestamp-router:visited {
    color: $text-color;
    text-decoration: none;
}

.timestamp-router:link {
    color: $text-color;
    text-decoration: none;
}

.message {
    color: $text-color_light;
    background-color: $bubble-two-color;
    padding: var(--unit);
    font-size: calc(var(--unit) * 2.5);
    border-radius: var(--unit);
    width: 100%;
    text-align: center;
    display: flex;

    p {
        margin: auto;
        line-height: 1.2;
    }
}

.top-info {
    display: flex;
    justify-content: space-between;
    gap: 4em;
    padding: 0 2rem;
    align-items: center;
}

.container-topics {
    background: $bubble-two-color;
    border-radius: 15px;
    padding: 3%;
    display: flex;
    align-items: center;
}

.twitch {
    display: flex;
    gap: 2rem;
    justify-content: center;
    margin: 2rem 0;
}

.sessionId {
    position: absolute;
    color: white;
    font-size: calc(var(--unit) * 2);
    z-index: 999;
    display: flex;
    gap: calc(var(--unit) * 1);
    align-items: center;
    padding: .5em;

    span {
        color: var(--color-yellow);
    }

    input {
        width: calc(var(--unit) * 16);
    }

    input,
    button {
        height: calc(var(--unit) * 2);
        font-size: calc(var(--unit) * 1.5);
        padding: 0 calc(var(--unit) * .5);
        border-radius: calc(var(--unit) * .25);
        border-width: calc(var(--unit) * .1);
    }
}
</style>
